import type { Opts, TransformerKey, TransformResult, Val } from './Types.ts'
import { IDLE } from './Types.ts'
import type { SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { useDispatch, useSelector } from 'react-redux'
import type { State } from '../../../../Flux/Slice/index.ts'
import { useEffect, useMemo } from 'react'
import { transformerRegistry } from './Registry.ts'

export function useTransformer<K extends TransformerKey>(
  sourceId: SourceId,
  bucketKey: string | undefined,
  key: K,
  opts: Opts<K>
): TransformResult<Val<K>> {
  const def = transformerRegistry[key]
  const dispatch = useDispatch()

  const optsKey = useMemo(() => def.optsKey(opts), [def, opts])

  /* selectors need to be called in the same order always, so we always call all selectors, but with fallbacks */

  const selectorResult = useSelector((state: typeof State) =>
    def.kind === 'selector' ? def.select(state, sourceId, bucketKey, opts) : (IDLE as TransformResult<Val<K>>)
  )

  const cacheKey = useSelector((state: typeof State) =>
    def.kind === 'async' ? def.makeCacheKey(state, sourceId, bucketKey, opts, optsKey) : ''
  )

  const asyncEntry = useSelector((state: typeof State) =>
    def.kind === 'async' ? def.selectEntry(state, cacheKey) : (IDLE as TransformResult<Val<K>>)
  )

  useEffect(() => {
    if (def.kind !== 'async') return
    if (asyncEntry.status === 'ready' || asyncEntry.status === 'loading') return
    dispatch(def.request({ cacheKey, sourceId, bucketKey, opts }))
  }, [dispatch, def, cacheKey, sourceId, asyncEntry.status, optsKey, bucketKey])

  return def.kind === 'selector' ? selectorResult : asyncEntry
}
